import React, { useMemo, useRef } from 'react';
import {
  Background,
  Controls,
  Edge,
  MiniMap,
  Node,
  NodeTypes,
  ReactFlow,
  ReactFlowInstance,
  useEdgesState,
  useNodesState,
} from 'reactflow';
import { Box, useTheme } from '@mui/material';
import { alpha, type Palette } from '@mui/material/styles';
import { TaskEditorValue, TaskFlowErrors } from '@features/Admin/Agent/taskFlow/domain/contracts';
import {
  buildTaskFlowCanvasGraph,
  TaskFlowCanvasEdge,
  TaskFlowCanvasNode,
} from '@features/Admin/Agent/taskFlow/utils/task-flow-mapper';
import { TaskFlowNode, TaskFlowNodeData } from './TaskFlowNode';
import 'reactflow/dist/style.css';

const X_SPACING = 260;
const X_START = 80;
const FIT_VIEW_OPTIONS = { padding: 0.2, minZoom: 0.6, maxZoom: 1.2 };

type TaskFlowCanvasProps = {
  tasks: TaskEditorValue[];
  errors: TaskFlowErrors;
  selectedTaskKey: string | null;
  onSelectTask: (key: string) => void;
  onReorderTask: (fromIndex: number, toIndex: number) => void;
  disabled?: boolean;
  height?: number | string | { xs: number; sm: number };
};

const nodeTypes: NodeTypes = { taskFlowNode: TaskFlowNode };

const toReactFlowNodes = (
  nodes: TaskFlowCanvasNode[],
  errors: TaskFlowErrors,
): Node<TaskFlowNodeData>[] =>
  nodes.map((node) => ({
    id: node.id,
    position: node.position,
    type: 'taskFlowNode',
    data: {
      task: node.data.task,
      index: node.data.index,
      hasError: Boolean(errors.taskErrors[node.data.task.clientKey]),
    },
  }));

const toReactFlowEdges = (edges: TaskFlowCanvasEdge[], palette: Palette): Edge[] =>
  edges.map((edge) => ({
    ...edge,
    type: 'default',
    style: {
      strokeWidth: 1.75,
      stroke: palette.mode === 'dark' ? palette.canvas.edge : palette.status.neutral.foreground,
    },
  }));

export const TaskFlowCanvas: React.FC<TaskFlowCanvasProps> = ({
  tasks,
  errors,
  selectedTaskKey,
  onSelectTask,
  onReorderTask,
  disabled = false,
  height = 420,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isDraggingRef = useRef(false);
  const graph = useMemo(() => buildTaskFlowCanvasGraph(tasks), [tasks]);
  const reactFlowInstanceRef = useRef<ReactFlowInstance | null>(null);
  const previousNodeIdsRef = useRef(new Set(graph.nodes.map((node) => node.id)));

  const [nodes, setNodes, onNodesChange] = useNodesState(toReactFlowNodes(graph.nodes, errors));
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    toReactFlowEdges(graph.edges, theme.palette),
  );

  React.useEffect(() => {
    if (isDraggingRef.current) return;
    setNodes(toReactFlowNodes(graph.nodes, errors));
  }, [graph.nodes, errors, setNodes]);

  React.useEffect(() => {
    setEdges(toReactFlowEdges(graph.edges, theme.palette));
  }, [graph.edges, theme.palette, setEdges]);

  React.useEffect(() => {
    setNodes((prev) => prev.map((node) => ({ ...node, selected: node.id === selectedTaskKey })));
  }, [selectedTaskKey, setNodes]);

  React.useEffect(() => {
    const hasAddedNode = graph.nodes.some((node) => !previousNodeIdsRef.current.has(node.id));
    previousNodeIdsRef.current = new Set(graph.nodes.map((node) => node.id));
    if (!hasAddedNode) return;

    // Wait for React Flow to render and measure the added node before fitting the graph.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        reactFlowInstanceRef.current?.fitView(FIT_VIEW_OPTIONS);
      });
    });
  }, [graph.nodes]);

  return (
    <Box
      data-testid="task-flow-canvas"
      data-editable={disabled ? 'false' : 'true'}
      sx={{
        width: '100%',
        height,
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: 1,
        backgroundColor: (t) => t.palette.background.default,
        overflow: 'hidden',
      }}>
      <ReactFlow
        onInit={(instance) => {
          reactFlowInstanceRef.current = instance;
        }}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={(_, node) => {
          onSelectTask(node.id);
        }}
        onNodeDragStart={() => {
          isDraggingRef.current = true;
        }}
        onNodeDragStop={(_, node) => {
          isDraggingRef.current = false;
          if (disabled) return;
          const currentIndex = graph.nodes.findIndex((n) => n.id === node.id);
          if (currentIndex === -1) return;
          const targetIndex = Math.max(
            0,
            Math.min(tasks.length - 1, Math.round((node.position.x - X_START) / X_SPACING)),
          );
          if (targetIndex !== currentIndex) {
            onReorderTask(currentIndex, targetIndex);
          } else {
            // Snap back to the canonical position for this order.
            setNodes(toReactFlowNodes(graph.nodes, errors));
          }
        }}
        nodesDraggable={!disabled}
        nodesConnectable={false}
        elementsSelectable
        panOnDrag
        fitView
        fitViewOptions={FIT_VIEW_OPTIONS}
        minZoom={0.5}
        nodeTypes={nodeTypes}>
        <MiniMap
          pannable
          zoomable
          style={{
            backgroundColor: isDarkMode
              ? theme.palette.canvas.background
              : theme.palette.background.default,
            border: `1px solid ${theme.palette.divider}`,
            width: 120,
            height: 80,
            borderRadius: theme.shape.borderRadius,
          }}
        />
        <Controls />
        <Background
          color={
            isDarkMode
              ? alpha(theme.palette.extendedGrey.neutral, 0.2)
              : alpha(theme.palette.common.black, 0.06)
          }
          gap={24}
          size={1}
        />
      </ReactFlow>
    </Box>
  );
};
